import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Button, Input, Select, Dialog, Tooltip, MenuItem } from '@dsi/react-eds';
import { useUserForm } from './user-form';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import styles from './EditUserDialog.module.css';
import { useLoader } from '../../../hooks/useLoader';
import api from '../../../api/axiosInstance';
import { AxiosError } from 'axios';
import { AuthTypes, RoleDropdownOptions, UserRoles } from '../../../const/UserManagementTypes';
import ReactDOM from 'react-dom';

export interface EditUserDialogProps {
  open: boolean;
  initialUser: { email: string; role: string; id: string; roles: string[] } | null;
  onSave: (user: { email: string; roles: string[]; role: string; id: string }) => void;
  onClose: () => void;
}

type UserInfo = {
  username?: string;
  displayName?: string;
  email?: string;
  roles?: string[];
  authType?: string;
};

const EditUserDialog: React.FC<EditUserDialogProps> = ({ open, initialUser, onSave, onClose }) => {
  const {
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
    control
  } = useUserForm({ initialUser: initialUser || { email: '', role: '', id: '', roles: [] } });
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormSubmit = async (data: any) => {
    showLoader();
    const updatedRoles: UserInfo = { roles: [UserRoles.Operator, UserRoles.Admin], email: data.email, username: data.email };
    if (data.role === RoleDropdownOptions.Operator) updatedRoles.roles = [UserRoles.Operator];
    if (data.authType === AuthTypes.Local) updatedRoles.displayName = data.email;
    try {
      const response = await api.patch(`api/keycloak/users/${data.id}`, updatedRoles);
      const updatedUser = {
        ...data,
        roles: response.data.roles || [],
        role: response.data.roles?.includes('admin') ? RoleDropdownOptions.Admin : RoleDropdownOptions.Operator
      };
      onSave(updatedUser);
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: `User ${data.email} was successfully updated!`,
          timestamp: new Date(),
          color: 'green'
        }
      ]);
    } catch (error: unknown) {
      let errorMsg = `An error occurred while adding the user ${data.email}.`;
      if (error instanceof AxiosError) {
        errorMsg = error?.response?.data?.message || error?.message;
      }
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: errorMsg,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  };

  useEffect(() => {
    if (open && initialUser) {
      reset(initialUser);
    }

    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, initialUser, reset]);

  return ReactDOM.createPortal(
    <ReactEDSWrapper
      component={Dialog}
      show={open}
      onClose={onClose}
      title="Edit User"
      subtitle="User Management"
      bottom={
        <ReactEDSWrapper
          component={Button}
          variant="primary"
          className="btn primary"
          type="submit"
          form="edit-user-form"
          disabled={!isDirty || !isValid}
        >
          Save
        </ReactEDSWrapper>
      }
    >
      <form id="edit-user-form" onSubmit={handleSubmit(handleFormSubmit)} className={styles.editUserForm}>
        <label htmlFor="edit-user-email" className={`${styles.whiteText} ${styles.editUserLabel}`}>
          Email <span className={styles.requiredAsterisk}> *</span>
          <ReactEDSWrapper component={Tooltip} message="Enter a valid Ericsson/Customer email address." pointer>
            <span className={styles.infoIcon}>
              <i className="icon icon-info" />
            </span>
          </ReactEDSWrapper>
        </label>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <ReactEDSWrapper
                component={Input}
                id="edit-user-email"
                placeholder="User email"
                {...field}
                className={styles.inputFieldEmail}
              />
              <div className={styles.errorContainer}>
                {error ? (
                  <span className={styles.error}>
                    <span className={styles.dangerIcon} aria-label="error" role="img">
                      &#9888;
                    </span>
                    {error.message}
                  </span>
                ) : null}
              </div>
            </>
          )}
        />
        <label htmlFor="role" className={`${styles.whiteText} ${styles.editUserLabel}`}>
          Role <span className={styles.requiredAsterisk}> *</span>
          <ReactEDSWrapper component={Tooltip} message={'Admin: full access and Operator: read only user.'} pointer>
            <span className={styles.infoIcon}>
              <i className="icon icon-info" />
            </span>
          </ReactEDSWrapper>
        </label>
        <Controller
          name="role"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <ReactEDSWrapper
                component={Select}
                id="role"
                label="Select option"
                {...field}
                onChange={(value) => {
                  const selected = Array.isArray(value) ? value[0] : value;
                  field.onChange(selected);
                }}
                type="single"
                className={styles.selectField}
              >
                <ReactEDSWrapper component={MenuItem} value={RoleDropdownOptions.Admin}>
                  {RoleDropdownOptions.Admin}
                </ReactEDSWrapper>
                <ReactEDSWrapper component={MenuItem} value={RoleDropdownOptions.Operator}>
                  {RoleDropdownOptions.Operator}
                </ReactEDSWrapper>
              </ReactEDSWrapper>
              <div className={styles.errorContainer}>
                {error ? (
                  <span className={styles.error}>
                    <span className={styles.dangerIcon} aria-label="error" role="img">
                      &#9888;
                    </span>
                    {error.message}
                  </span>
                ) : null}
              </div>
            </>
          )}
        />
      </form>
    </ReactEDSWrapper>,
    document.body
  );
};

export default EditUserDialog;
